<template>
  <div class="tender-contractors-panel">
    <!-- Panel Header -->
    <div class="panel-header">
      <div class="row items-center">
        <div class="col">
          <div class="panel-title">
            <q-icon name="business" color="primary" class="q-mr-sm" />
            Tender Contractors
          </div>
          <div class="panel-subtitle">
            {{ contractors.length }} contractor{{ contractors.length !== 1 ? 's' : '' }} added to this tender
          </div>
        </div>
        <div class="col-auto">
          <q-btn
            color="primary"
            icon="add"
            label="Add Contractor"
            class="add-btn"
            @click="$emit('add-contractor')"
          />
        </div>
      </div>
    </div>

    <!-- Contractors List -->
    <div v-if="contractors.length === 0" class="empty-state">
      <q-icon name="business_center" size="4em" color="grey-5" />
      <div class="empty-title">No Contractors Added</div>
      <div class="empty-subtitle">
        Add contractors to this tender to start the bidding process
      </div>
      <q-btn
        color="primary"
        icon="add"
        label="Add First Contractor"
        class="q-mt-md"
        @click="$emit('add-contractor')"
      />
    </div>

    <div v-else class="contractors-grid">
      <div
        v-for="contractor in contractors"
        :key="contractor.id"
        class="contractor-card"
      >
        <q-card flat bordered>
          <!-- Contractor Header -->
          <q-card-section class="contractor-header">
            <div class="row items-center">
              <div class="col">
                <div class="contractor-name">
                  {{ contractor.effective_company_name || contractor.business_name || 'Unknown Company' }}
                  <q-badge
                    v-if="contractor.is_primary"
                    color="primary"
                    label="Primary"
                    class="q-ml-sm"
                  />
                </div>
                <div class="contractor-contact">
                  <q-icon name="person" size="16px" class="q-mr-xs" />
                  {{ getContactName(contractor) }}
                  <span v-if="getContactRole(contractor)" class="contact-role">
                    ({{ getContactRole(contractor) }})
                  </span>
                </div>
              </div>
              <div class="col-auto">
                <q-badge
                  :color="getContractorStatusColor(contractor.status)"
                  :label="getContractorStatusLabel(contractor.status)"
                  class="status-badge"
                />
              </div>
            </div>
          </q-card-section>

          <!-- Contractor Details -->
          <q-card-section class="contractor-details">
            <div class="details-grid">
              <div class="detail-item">
                <q-icon name="email" size="16px" color="grey-6" />
                <span class="detail-label">Email:</span>
                <span class="detail-value">{{ getContactEmail(contractor) || 'Not provided' }}</span>
              </div>
              
              <div class="detail-item">
                <q-icon name="phone" size="16px" color="grey-6" />
                <span class="detail-label">Phone:</span>
                <span class="detail-value">{{ getContactPhone(contractor) || 'Not provided' }}</span>
              </div>
              
              <div v-if="contractor.quote_value" class="detail-item">
                <q-icon name="attach_money" size="16px" color="positive" />
                <span class="detail-label">Quote Value:</span>
                <span class="detail-value text-positive font-weight-bold">
                  ${{ formatCurrency(contractor.quote_value) }}
                </span>
              </div>
              
              <div v-if="contractor.response_date" class="detail-item">
                <q-icon name="schedule" size="16px" color="grey-6" />
                <span class="detail-label">Responded:</span>
                <span class="detail-value">{{ formatDate(contractor.response_date) }}</span>
              </div>
            </div>

            <!-- Contractor Notes -->
            <div v-if="contractor.notes" class="contractor-notes">
              <div class="notes-label">
                <q-icon name="note" size="16px" class="q-mr-xs" />
                Notes
              </div>
              <div class="notes-text">{{ contractor.notes }}</div>
            </div>
          </q-card-section>

          <!-- Contractor Actions -->
          <q-card-actions class="contractor-actions">
            <q-btn
              flat
              icon="email"
              label="Send Email"
              color="primary"
              size="sm"
              @click="$emit('send-email', contractor)"
            />
            
            <q-btn
              flat
              icon="attach_file"
              label="Documents"
              color="secondary"
              size="sm"
              @click="$emit('upload-document', contractor)"
            />
            
            <q-space />
            
            <q-btn-group flat>
              <q-btn
                flat
                round
                dense
                icon="edit"
                color="primary"
                @click="$emit('edit-contractor', contractor)"
              >
                <q-tooltip>Edit Contractor</q-tooltip>
              </q-btn>
              
              <q-btn
                flat
                round
                dense
                icon="delete"
                color="negative"
                @click="confirmDelete(contractor)"
              >
                <q-tooltip>Remove Contractor</q-tooltip>
              </q-btn>
            </q-btn-group>
          </q-card-actions>

          <!-- Progress Indicator -->
          <div class="contractor-progress">
            <q-linear-progress
              :value="getContractorProgress(contractor)"
              :color="getContractorProgressColor(contractor)"
              size="3px"
            />
          </div>
        </q-card>
      </div>
    </div>

    <!-- Summary Statistics -->
    <div v-if="contractors.length > 0" class="contractors-summary q-mt-lg">
      <q-card flat bordered>
        <q-card-section>
          <div class="summary-header">
            <q-icon name="analytics" color="accent" class="q-mr-sm" />
            <div class="summary-title">Contractor Summary</div>
          </div>
          
          <div class="summary-stats">
            <div class="stat-item">
              <div class="stat-number">{{ contractors.length }}</div>
              <div class="stat-label">Total Contractors</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-number">{{ respondedCount }}</div>
              <div class="stat-label">Responded</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-number">{{ pendingCount }}</div>
              <div class="stat-label">Pending</div>
            </div>
            
            <div class="stat-item">
              <div class="stat-number">${{ formatCurrency(averageQuoteValue) }}</div>
              <div class="stat-label">Avg Quote Value</div>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </div>

    <!-- Delete Confirmation Dialog -->
    <q-dialog v-model="showDeleteDialog" persistent>
      <q-card>
        <q-card-section class="row items-center">
          <q-avatar icon="warning" color="negative" text-color="white" />
          <span class="q-ml-sm text-h6">Remove Contractor</span>
        </q-card-section>
        
        <q-card-section>
          <p>Are you sure you want to remove <strong>{{ contractorToDelete?.effective_company_name || contractorToDelete?.business_name || 'this contractor' }}</strong> from this tender?</p>
          <p class="text-caption text-grey-7">
            This will remove the contractor and any associated quotes or communications.
          </p>
        </q-card-section>
        
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="Cancel" />
          <q-btn 
            label="Remove" 
            color="negative" 
            @click="deleteContractor" 
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, computed } from 'vue';
  import { useQuasar } from 'quasar';

  const props = defineProps({
    tenderId: {
      type: Number,
      required: true,
    },
    contractors: {
      type: Array,
      default: () => [],
    },
  });

  const emit = defineEmits([
    'add-contractor',
    'edit-contractor',
    'delete-contractor',
    'send-email',
    'upload-document',
  ]);

  const $q = useQuasar();

  // State
  const showDeleteDialog = ref(false);
  const contractorToDelete = ref(null);

  // Computed properties
  const respondedCount = computed(() => {
    return props.contractors.filter((c) => 
      c.status === 'submitted' || c.status === 'won' || c.status === 'lost',
    ).length;
  });

  const pendingCount = computed(() => {
    return props.contractors.filter((c) => c.status === 'pending').length;
  });

  const averageQuoteValue = computed(() => {
    const contractorsWithValues = props.contractors.filter((c) => c.quote_value);
    if (contractorsWithValues.length === 0) {return 0;}
  
    const total = contractorsWithValues.reduce((sum, c) => sum + Number(c.quote_value), 0);
    return total / contractorsWithValues.length;
  });

  // Methods
  function getContractorStatusColor(status) {
    const colors = {
      pending: 'orange',
      submitted: 'blue',
      won: 'positive',
      lost: 'negative',
      declined: 'grey',
    };
    return colors[status] || 'grey';
  }

  function getContractorStatusLabel(status) {
    const labels = {
      pending: 'Pending Response',
      submitted: 'Quote Submitted',
      won: 'Won',
      lost: 'Lost',
      declined: 'Declined',
    };
    return labels[status] || status || 'Unknown';
  }

  function getContractorProgress(contractor) {
    const progressMap = {
      pending: 0.2,
      submitted: 0.7,
      won: 1.0,
      lost: 1.0,
      declined: 0.5,
    };
    return progressMap[contractor.status] || 0;
  }

  function getContractorProgressColor(contractor) {
    const colorMap = {
      pending: 'orange',
      submitted: 'blue', 
      won: 'positive',
      lost: 'negative',
      declined: 'grey',
    };
    return colorMap[contractor.status] || 'grey';
  }

  function formatDate(dateString) {
    if (!dateString) {return '';}
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric', 
    });
  }

  function formatCurrency(amount) {
    return Number(amount).toLocaleString('en-AU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  function confirmDelete(contractor) {
    contractorToDelete.value = contractor;
    showDeleteDialog.value = true;
  }

  function deleteContractor() {
    if (contractorToDelete.value) {
      emit('delete-contractor', contractorToDelete.value);
      showDeleteDialog.value = false;
      contractorToDelete.value = null;
    }
  }

  function getContactName(contractor) {
    // Use effective_contact_name if available (new system)
    if (contractor.effective_contact_name) {
      return contractor.effective_contact_name;
    }
    // Fallback to legacy contact
    if (contractor.contact_id && contractor.contact) {
      return contractor.contact.name || 'Contact not found';
    }
    // Check organization_contact
    if (contractor.organization_contact) {
      return contractor.organization_contact.name || 'Contact not found';
    }
    return 'No contact assigned';
  }

  function getContactRole(contractor) {
    // Check organization_contact first (new system)
    if (contractor.organization_contact) {
      return contractor.organization_contact.role || null;
    }
    // Fallback to legacy contact
    if (contractor.contact_id && contractor.contact) {
      return contractor.contact.role || null;
    }
    return null;
  }

  function getContactEmail(contractor) {
    // Use effective_email if available (new system)
    if (contractor.effective_email) {
      return contractor.effective_email;
    }
    // Fallback to legacy contact
    if (contractor.contact_id && contractor.contact) {
      return contractor.contact.email || null;
    }
    // Check organization_contact
    if (contractor.organization_contact) {
      return contractor.organization_contact.email || null;
    }
    return null;
  }

  function getContactPhone(contractor) {
    // Use effective_phone if available (new system)
    if (contractor.effective_phone) {
      return contractor.effective_phone;
    }
    // Fallback to legacy contact
    if (contractor.contact_id && contractor.contact) {
      return contractor.contact.phone || null;
    }
    // Check organization_contact
    if (contractor.organization_contact) {
      return contractor.organization_contact.phone || null;
    }
    return null;
  }
</script>

<style lang="scss" scoped>
.tender-contractors-panel {
  .panel-header {
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
    
    .panel-title {
      font-size: 1.5rem;
      font-weight: 700;
      color: #333;
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }
    
    .panel-subtitle {
      color: #666;
      font-size: 0.9rem;
    }
    
    .add-btn {
      font-weight: 600;
      border-radius: 8px;
    }
  }
  
  .empty-state {
    text-align: center;
    padding: 48px 24px;
    color: #666;
    
    .empty-title {
      font-size: 1.2rem;
      font-weight: 600;
      margin: 16px 0 8px;
    }
    
    .empty-subtitle {
      font-size: 0.9rem;
      line-height: 1.4;
    }
  }
  
  .contractors-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
    gap: 20px;
    
    .contractor-card {
      position: relative;
      
      .contractor-header {
        padding-bottom: 12px;
        border-bottom: 1px solid #f0f0f0;
        
        .contractor-name {
          font-weight: 700;
          font-size: 1.1rem;
          color: #333;
          display: flex;
          align-items: center;
          margin-bottom: 4px;
        }
        
        .contractor-contact {
          font-size: 0.9rem;
          color: #666;
          display: flex;
          align-items: center;
          
          .contact-role {
            margin-left: 4px;
            color: #999;
            font-style: italic;
          }
        }
        
        .status-badge {
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.75rem;
        }
      }
      
      .contractor-details {
        .details-grid {
          display: grid;
          gap: 8px;
          margin-bottom: 16px;
          
          .detail-item {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 0.9rem;
            
            .detail-label {
              color: #666;
              font-weight: 500;
              min-width: 60px;
            }
            
            .detail-value {
              font-weight: 600;
              color: #333;
            }
          }
        }
        
        .contractor-notes {
          border-top: 1px solid #f0f0f0;
          padding-top: 12px;
          
          .notes-label {
            font-size: 0.85rem;
            color: #666;
            font-weight: 500;
            margin-bottom: 6px;
            display: flex;
            align-items: center;
          }
          
          .notes-text {
            font-size: 0.9rem;
            color: #333;
            line-height: 1.4;
            font-style: italic;
          }
        }
      }
      
      .contractor-actions {
        border-top: 1px solid #f0f0f0;
        padding-top: 8px;
      }
      
      .contractor-progress {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
      }
    }
  }
  
  .contractors-summary {
    .summary-header {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      
      .summary-title {
        font-size: 1.1rem;
        font-weight: 700;
        color: #333;
      }
    }
    
    .summary-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
      gap: 24px;
      
      .stat-item {
        text-align: center;
        
        .stat-number {
          font-size: 1.8rem;
          font-weight: 700;
          color: #1976d2;
          margin-bottom: 4px;
        }
        
        .stat-label {
          font-size: 0.85rem;
          color: #666;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
      }
    }
  }
}
</style>